import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import Alert from "../Alert";
import { modalStyle } from "../constants";
import { sendGetRequest } from "../../utils";

const LOAD_ERROR = "Failed to load webhook events. Please try again.";

const formatDate = (date) => (date ? new Date(date).toLocaleString() : "");

const describeResult = (row) => {
  if (row.error) return "Failed";
  if (row.action === "relink member") return "Member re-subscribed";
  if (row.action === "create member") return "Member signed up";
  if (row.action === "set status=1") {
    return row.rows_affected ? "Member activated" : "No matching member";
  }
  if (row.action === "set status=2") {
    return row.rows_affected ? "Member deactivated" : "No matching member";
  }
  return "Recorded only";
};

function StripeEvents() {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [filterByType, setFilterByType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [payload, setPayload] = useState(null);

  const doFetchEvents = () => {
    sendGetRequest("stripe-events", true)
      .then((res) => res.json())
      .then((data) => {
        if (data.errMessage) {
          setError(data.errMessage);
        } else {
          setError(null);
          setData(data);
        }
      })
      .catch(() => setError(LOAD_ERROR));
  };

  useEffect(() => {
    doFetchEvents();
  }, []);

  const doViewEvent = (row) => {
    setSelected(row);
    setPayload(null);
    sendGetRequest(`stripe-events/${row.id}`, true)
      .then((res) => res.json())
      .then((data) => {
        if (data.errMessage) {
          setPayload(data.errMessage);
        } else {
          setPayload(JSON.stringify(data.payload, null, 2));
        }
      })
      .catch(() => setPayload(LOAD_ERROR));
  };

  const types = [...new Set(data.map((row) => row.type))].sort();

  const query = searchQuery.trim().toLowerCase();
  const filteredData = data.filter((row) => {
    if (filterByType !== "all" && row.type !== filterByType) return false;
    if (!query) return true;
    return [
      row.type,
      row.subscriber_name,
      row.subscriber_phone,
      row.subscription_id,
      row.customer_id,
      row.event_id,
    ].some((field) => field && field.toLowerCase().includes(query));
  });

  return (
    <div>
      {selected ? (
        <Modal open onClose={() => setSelected(null)}>
          <Box sx={{ ...modalStyle, width: 720, maxWidth: "95vw" }}>
            <Typography variant="h6">{selected.type}</Typography>
            <p className="text-sm text-gray-600 break-all">
              {selected.event_id} · received {formatDate(selected.received_at)}
            </p>
            {selected.error ? (
              <Alert error={selected.error} containerClassName="mt-2 mb-0" />
            ) : null}
            <pre className="mt-4 p-3 bg-slate-100 rounded-md text-xs overflow-auto max-h-[60vh]">
              {payload ?? "Loading..."}
            </pre>
            <div className="flex justify-end mt-4">
              <Button
                type="button"
                variant="contained"
                onClick={() => setSelected(null)}
              >
                Close
              </Button>
            </div>
          </Box>
        </Modal>
      ) : null}

      <Box sx={{ paddingTop: 2, flexGrow: 1 }} className="shadow-lg border">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert error={error} containerClassName="mt-0 mb-2" />
            <TableContainer
              component={Paper}
              className="!border-none !shadow-none"
            >
              <Typography variant="h4" className="p-4">
                Stripe Webhooks
              </Typography>
              <div className="flex items-center p-4 gap-x-4">
                <div>
                  <span className="mr-1">Filter By Event:</span>
                  <select
                    className="border p-1 rounded-md border-gray-400 min-w-28"
                    onChange={(e) => setFilterByType(e.target.value)}
                  >
                    <option value="all">All</option>
                    {types.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className="mr-1">Search:</span>
                  <input
                    type="text"
                    placeholder="Name, phone, event, Stripe ID"
                    className="border p-1 rounded-md border-gray-400 min-w-48"
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={doFetchEvents}
                  >
                    Refresh
                  </Button>
                </div>
              </div>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell className="!font-semibold">Received</TableCell>
                    <TableCell className="!font-semibold">Event</TableCell>
                    <TableCell className="!font-semibold">Member</TableCell>
                    <TableCell className="!font-semibold">
                      Subscription ID
                    </TableCell>
                    <TableCell className="!font-semibold">
                      Stripe Status
                    </TableCell>
                    <TableCell className="!font-semibold">Result</TableCell>
                    <TableCell className="!font-semibold">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    <>
                      {filteredData.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell size="small">
                            {formatDate(row.received_at)}
                          </TableCell>
                          <TableCell size="small">{row.type}</TableCell>
                          <TableCell size="small">
                            {row.subscriber_name ? (
                              <>
                                <div>{row.subscriber_name}</div>
                                <div className="text-gray-600">
                                  {row.subscriber_phone}
                                </div>
                              </>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell size="small">
                            {row.subscription_id}
                          </TableCell>
                          <TableCell size="small">
                            {row.stripe_status}
                          </TableCell>
                          <TableCell size="small">
                            {describeResult(row)}
                          </TableCell>
                          <TableCell size="small">
                            <Button
                              type="button"
                              variant="contained"
                              onClick={() => doViewEvent(row)}
                              size="small"
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} size="medium">
                        <div className="text-center">No record found.</div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}

export default StripeEvents;
